import { useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Modal,
  Select,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { MetaTags } from '@redwoodjs/web'

import AvatarGroup from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_API_CLIENT_ACCESS,
  DELETE_API_CLIENT_ACCESS,
  GET_ADMIN_COMPANIES,
  GET_API_CLIENT_ACCESSES,
  UPDATE_API_CLIENT_ACCESS,
} from 'src/graphql/apiClientAccesses.graphql'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconClock from 'src/icons/IconClock'
import IconCopy from 'src/icons/IconCopy'
import IconEdit from 'src/icons/IconEdit'
import IconExternalLink from 'src/icons/IconExternalLink'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import IconTrash from 'src/icons/IconTrash'

import ApiClientAccessFormModal from './components/ApiClientAccessFormModal'
import type { ApiClientAccessFormRow } from './components/ApiClientAccessFormModal'
import ApiClientAccessHistoryDrawer from './components/ApiClientAccessHistoryDrawer'
import CompanySelect from './components/CompanySelect'

const DEFAULT_PAGE_SIZE = 20

type SortField = 'clientId' | 'companyName' | 'lastSeenAt'
type SortDirection = 'asc' | 'desc'

type StatusFilter = 'all' | 'active' | 'inactive'

type Filters = {
  clientId: string
  clientName: string
  companyId: string | null
  active: StatusFilter
}

const EMPTY_FILTERS: Filters = {
  clientId: '',
  clientName: '',
  companyId: null,
  active: 'all',
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

const ACTIVE_TOOLTIP =
  'Inactive rows are still allowed until enforcement is turned on.'

type Row = ApiClientAccessFormRow & {
  auth0Url: string | null
  lastSeenAt: string | null
  lastEditedBy: { firstName: string; lastName: string } | null
}

const toVariables = (filters: Filters) => ({
  clientId: filters.clientId.trim() || null,
  clientName: filters.clientName.trim() || null,
  companyId: filters.companyId || null,
  companyName: null,
  active: filters.active === 'all' ? null : filters.active === 'active',
})

const CopyableId = ({
  id,
  label,
  children,
}: {
  id: string
  label: string
  children?: React.ReactNode
}) => (
  <div className="flex items-center gap-1 font-mono text-doubleNickel-gray-500">
    <span className="whitespace-nowrap">{id}</span>
    <Tooltip label={`Copy ${label}`}>
      <ActionIcon
        size={18}
        variant="subtle"
        aria-label={`Copy ${label}`}
        onClick={() => {
          navigator.clipboard.writeText(id)
          toast(`Copied ${label} to clipboard`, 'success')
        }}
      >
        <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
      </ActionIcon>
    </Tooltip>
    {children}
  </div>
)

const AdminIntegrationApiClientsPage = () => {
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS)
  const [sort, setSort] = useState<{
    field: SortField
    direction: SortDirection
  }>({
    field: 'lastSeenAt',
    direction: 'desc',
  })
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [formOpened, formHandlers] = useDisclosure(false)
  const [editing, setEditing] = useState<Row | null>(null)
  const [removing, setRemoving] = useState<Row | null>(null)
  const [history, setHistory] = useState<Row | null>(null)
  const [toggling, setToggling] = useState<Row | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const variables = {
    filter: toVariables(applied),
    sort,
    page,
    pageSize,
  }

  const { data, loading, error, refetch } = useQuery(GET_API_CLIENT_ACCESSES, {
    variables,
    fetchPolicy: 'cache-and-network',
  })
  const { data: companiesData } = useQuery(GET_ADMIN_COMPANIES, {
    variables: { search: null },
  })

  const [create, { loading: creating }] = useMutation(
    CREATE_API_CLIENT_ACCESS,
    {
      onCompleted: ({ createApiClientAccess: result }) => {
        toast(
          `Added ${result.clientName ?? result.clientId} for ${
            result.company.companyName
          }`
        )
        closeForm()
        refetch()
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )
  const [update, { loading: updating }] = useMutation(
    UPDATE_API_CLIENT_ACCESS,
    {
      onCompleted: () => {
        toast('Row updated')
        closeForm()
        refetch()
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )
  const [setActive] = useMutation(UPDATE_API_CLIENT_ACCESS, {
    onCompleted: async ({ updateApiClientAccess: result }) => {
      toast(`Row set to ${result.active ? 'active' : 'inactive'}`)
      try {
        await refetch()
      } finally {
        setTogglingId(null)
      }
    },
    onError: (error) => {
      toast(error.message, 'error')
      setTogglingId(null)
    },
  })
  const [remove, { loading: deleting }] = useMutation(
    DELETE_API_CLIENT_ACCESS,
    {
      onCompleted: () => {
        toast('Row removed')
        setRemoving(null)
        if (rows.length === 1 && page > 0) {
          setPage(page - 1)
        } else {
          refetch()
        }
      },
      onError: (error) => toast(error.message, 'error'),
    }
  )

  const closeForm = () => {
    formHandlers.close()
    setEditing(null)
  }

  const applyFilters = () => {
    setApplied(draft)
    setPage(0)
  }

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS)
    setApplied(EMPTY_FILTERS)
    setPage(0)
  }

  const toggleSort = (field: SortField) => {
    setSort((current) =>
      current.field === field
        ? { field, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    )
    setPage(0)
  }

  const sortMarker = (field: SortField) =>
    sort.field === field ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ''

  const hasFilters =
    applied.clientId.trim() !== '' ||
    applied.clientName.trim() !== '' ||
    !!applied.companyId ||
    applied.active !== 'all'

  const rows: Row[] = data?.apiClientAccesses?.items ?? []
  const total: number = data?.apiClientAccesses?.total ?? 0
  const auth0ApplicationsUrl: string | null =
    data?.apiClientAccesses?.auth0ApplicationsUrl ?? null
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const companies = companiesData?.adminCompanies ?? []

  const onKeyDown = (event: React.KeyboardEvent) =>
    event.key === 'Enter' && applyFilters()

  const dim = (row: Row) => (row.active ? '' : 'opacity-50')

  return (
    <div className="flex h-full flex-col gap-5 pb-4 pr-6">
      <MetaTags
        title="API Clients"
        description="Which Auth0 API clients touch which companies"
      />

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            API Clients
          </div>
          {auth0ApplicationsUrl && (
            <Button
              component="a"
              href={auth0ApplicationsUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="subtle"
              size="compact-sm"
              rightSection={
                <IconExternalLink className="h-3.5 w-3.5 fill-none stroke-current" />
              }
            >
              Auth0 applications
            </Button>
          )}
        </div>
        <div className="max-w-3xl text-sm text-doubleNickel-gray-600">
          Every Auth0 machine-to-machine client that has called the public API,
          the company it asked for, and the scope it used. Rows appear on their
          own as partners call us; you can also add, edit or remove them by
          hand. Nothing here blocks a request yet.
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <TextInput
          id="api-clients-filter-client-id"
          label="Client id"
          placeholder="abc123"
          className="min-w-[14rem]"
          value={draft.clientId}
          onChange={(event) =>
            setDraft({ ...draft, clientId: event.currentTarget.value })
          }
          onKeyDown={onKeyDown}
        />
        <TextInput
          id="api-clients-filter-client-name"
          label="Client name"
          placeholder="Hire Truckers"
          className="min-w-[14rem]"
          value={draft.clientName}
          onChange={(event) =>
            setDraft({ ...draft, clientName: event.currentTarget.value })
          }
          onKeyDown={onKeyDown}
        />
        <CompanySelect
          id="api-clients-filter-company"
          label="Company"
          placeholder="Name or id"
          className="min-w-[20rem]"
          clearable
          companies={companies}
          value={draft.companyId}
          onChange={(value) => setDraft({ ...draft, companyId: value })}
        />
        <Select
          id="api-clients-filter-active"
          label="Status"
          className="w-32"
          allowDeselect={false}
          data={STATUS_OPTIONS}
          value={draft.active}
          onChange={(value) =>
            setDraft({ ...draft, active: (value as StatusFilter) ?? 'all' })
          }
        />
        <Button onClick={applyFilters} loading={loading}>
          Filter
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setEditing(null)
            formHandlers.open()
          }}
        >
          Add client
        </Button>
        {hasFilters && (
          <Button variant="subtle" onClick={clearFilters}>
            Clear
          </Button>
        )}
      </div>

      {error ? (
        <div className="text-sm text-doubleNickel-error-600">
          Could not load API clients: {error.message}
        </div>
      ) : loading && !data ? (
        <div className="text-sm text-doubleNickel-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-doubleNickel-gray-500">
          {hasFilters
            ? 'No API clients match those filters.'
            : 'Nothing recorded yet. Rows appear as partners call the API.'}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-doubleNickel-gray-200">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 z-10 bg-doubleNickel-gray-50 text-doubleNickel-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">
                  <button type="button" onClick={() => toggleSort('clientId')}>
                    Client{sortMarker('clientId')}
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">
                  <button
                    type="button"
                    onClick={() => toggleSort('companyName')}
                  >
                    Company{sortMarker('companyName')}
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">Scopes</th>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  Last editor
                </th>
                <th className="px-3 py-2 font-medium">Active</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.apiClientAccessId}
                  className="border-t border-doubleNickel-gray-200"
                >
                  <td className={`px-3 py-2 ${dim(row)}`}>
                    <div className="flex items-center gap-1.5">
                      {row.clientStatus === 'KNOWN' ? (
                        <span className="font-medium text-doubleNickel-gray-900">
                          {row.clientName || row.clientId}
                        </span>
                      ) : row.clientStatus === 'UNKNOWN' ? (
                        <>
                          <span className="font-medium text-doubleNickel-gray-900">
                            Unknown client
                          </span>
                          <Tooltip label="This client id does not exist in Auth0">
                            <span aria-label="Unknown client" role="img">
                              <IconAlertTriangle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-warning-600" />
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <span className="font-medium text-doubleNickel-gray-900">
                            Unverified client
                          </span>
                          <Tooltip label="Auth0 could not be reached, so this client id was not checked. Try again in a moment.">
                            <span aria-label="Unverified client" role="img">
                              <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>
                    <CopyableId id={row.clientId} label="client id">
                      {row.auth0Url && (
                        <Tooltip label="Open this application in Auth0">
                          <ActionIcon
                            component="a"
                            href={row.auth0Url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size={18}
                            variant="subtle"
                            aria-label="Open in Auth0"
                          >
                            <IconExternalLink className="fill-none stroke-doubleNickel-gray-500" />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyableId>
                  </td>
                  <td className={`px-3 py-2 ${dim(row)}`}>
                    <div className="font-medium text-doubleNickel-gray-900">
                      {row.company.companyName}
                    </div>
                    <CopyableId id={row.company.companyId} label="company id" />
                  </td>
                  <td className={`px-3 py-2 ${dim(row)}`}>
                    <div className="flex flex-wrap gap-1">
                      {row.scopes.map((scope) => (
                        <Tooltip key={scope} label={scope}>
                          <Badge size="sm" variant="light">
                            {scope}
                          </Badge>
                        </Tooltip>
                      ))}
                    </div>
                  </td>
                  <td className={`px-3 py-2 ${dim(row)}`}>
                    {row.lastEditedBy ? (
                      <AvatarGroup avatars={[row.lastEditedBy]} />
                    ) : (
                      <Tooltip label="Written by the public API, no admin has edited it">
                        <Avatar
                          size="sm"
                          radius="sm"
                          classNames={{
                            placeholder:
                              'bg-doubleNickel-success-50 text-doubleNickel-success-700',
                          }}
                        >
                          API
                        </Avatar>
                      </Tooltip>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Tooltip label={ACTIVE_TOOLTIP}>
                      <div className="inline-flex">
                        <Switch
                          size="xs"
                          checked={row.active}
                          disabled={togglingId === row.apiClientAccessId}
                          aria-label={`Active for ${row.company.companyName}`}
                          onChange={() => setToggling(row)}
                        />
                      </div>
                    </Tooltip>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <Tooltip label="History">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          aria-label="History"
                          onClick={() => setHistory(row)}
                        >
                          <IconClock className="h-4 w-4 fill-none stroke-doubleNickel-gray-600" />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Edit">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          aria-label="Edit"
                          onClick={() => {
                            setEditing(row)
                            formHandlers.open()
                          }}
                        >
                          <IconEdit className="h-4 w-4 fill-none stroke-doubleNickel-gray-600" />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          aria-label="Delete"
                          onClick={() => setRemoving(row)}
                        >
                          <IconTrash className="h-4 w-4 fill-none stroke-doubleNickel-error-600" />
                        </ActionIcon>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <PaginationArea
          page={page + 1}
          totalPages={totalPages}
          resultsPerPage={pageSize}
          setResultsPerPage={(value) => {
            setPageSize(value)
            setPage(0)
          }}
          onPageChange={(value) => setPage(value - 1)}
        />
      )}

      <ApiClientAccessFormModal
        opened={formOpened}
        onClose={closeForm}
        mode={editing ? 'edit' : 'create'}
        row={editing ?? undefined}
        submitting={creating || updating}
        onSubmit={(values) =>
          editing
            ? update({
                variables: {
                  apiClientAccessId: editing.apiClientAccessId,
                  input: { scopes: values.scopes, active: values.active },
                },
              })
            : create({
                variables: {
                  input: {
                    clientId: values.clientId,
                    companyId: values.companyId,
                    scopes: values.scopes,
                  },
                },
              })
        }
      />

      <ApiClientAccessHistoryDrawer
        row={history}
        onClose={() => setHistory(null)}
      />

      <Modal
        opened={!!toggling}
        onClose={() => setToggling(null)}
        title={
          toggling
            ? `Set ${toggling.clientName ?? toggling.clientId} ${
                toggling.active ? 'inactive' : 'active'
              } for ${toggling.company.companyName}?`
            : ''
        }
        centered
      >
        <div className="flex flex-col gap-4 text-sm">
          <div className="text-doubleNickel-gray-700">
            {toggling?.active
              ? 'The row stays and can be switched back any time. The client keeps working for this company until enforcement is turned on.'
              : 'The client will be allowed for this company once enforcement is turned on.'}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="subtle" onClick={() => setToggling(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!toggling) return
                setTogglingId(toggling.apiClientAccessId)
                setActive({
                  variables: {
                    apiClientAccessId: toggling.apiClientAccessId,
                    input: { active: !toggling.active },
                  },
                })
                setToggling(null)
              }}
            >
              {toggling?.active ? 'Set inactive' : 'Set active'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        opened={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove this row?"
        centered
      >
        <div className="flex flex-col gap-4 text-sm">
          <div className="text-doubleNickel-gray-700">
            If the client calls the API again for this company, the row comes
            back on its own. Blocking is not enabled yet.
          </div>
          <div className="text-doubleNickel-gray-700">
            To pause a client without losing the row, switch it to inactive
            instead.
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="subtle" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleting}
              onClick={() =>
                removing &&
                remove({
                  variables: { apiClientAccessId: removing.apiClientAccessId },
                })
              }
            >
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AdminIntegrationApiClientsPage
